import React, { useEffect, useMemo, useState } from "react";
import axios from "utils/axios";
import { GET_VENDOR_TAXONOMY_URL } from "config/services";
import { lookupVendorMakes } from "services/vendorMakeService";

let cachedVendorTaxonomy = null;
let vendorTaxonomyRequest = null;

const getVendorTaxonomy = async () => {
  if (cachedVendorTaxonomy) return cachedVendorTaxonomy;

  if (!vendorTaxonomyRequest) {
    vendorTaxonomyRequest = axios
      .get(GET_VENDOR_TAXONOMY_URL)
      .then((response) => {
        cachedVendorTaxonomy = response?.data?.data || {};
        return cachedVendorTaxonomy;
      })
      .catch((error) => {
        vendorTaxonomyRequest = null;
        throw error;
      });
  }

  return vendorTaxonomyRequest;
};

const hasItems = (items) => Array.isArray(items) && items.length > 0;

const TaxonomySummaryIcon = ({ type }) => {
  const paths = {
    divisions: (
      <>
        <path d="M4 7.5 12 3l8 4.5-8 4.5-8-4.5Z" />
        <path d="M4 7.5V16l8 5 8-5V7.5M12 12v9" />
      </>
    ),
    categories: (
      <>
        <path d="M9 6h11M9 12h11M9 18h11" />
        <circle cx="4" cy="6" r="1" fill="currentColor" stroke="none" />
        <circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" />
        <circle cx="4" cy="18" r="1" fill="currentColor" stroke="none" />
      </>
    ),
    subCategories: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="8.5" y="14" width="7" height="7" rx="1" />
        <path d="M6.5 10v2h11v-2M12 12v2" />
      </>
    ),
    brands: (
      <>
        <path d="M20 13 13 20l-9-9V4h7l9 9Z" />
        <circle cx="8.5" cy="8.5" r="1.5" />
      </>
    ),
  };

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      {paths[type]}
    </svg>
  );
};

const isSameId = (left, right) => String(left || "") === String(right || "");

const getDiscipline = (taxonomyType, disciplineId) =>
  taxonomyType?.disciplines?.find((discipline) => isSameId(discipline?._id, disciplineId));

const getCategory = (discipline, categoryId) =>
  discipline?.categories?.find((category) => isSameId(category?._id, categoryId));

const getSubCategory = (category, subCategoryId) =>
  category?.subCategories?.find((subCategory) => isSameId(subCategory?._id, subCategoryId));

const buildMappedVendorCategories = (vendorCategories, taxonomyAll) =>
  vendorCategories
    .map((typeEntry) => {
      const vendorType = typeEntry?.vendorType || "";
      const taxonomyType = taxonomyAll?.[vendorType];

      const disciplines = (typeEntry?.disciplines || [])
        .map((disciplineEntry) => {
          const discipline = getDiscipline(
            taxonomyType,
            disciplineEntry?.disciplineId,
          );

          if (!discipline?.name) return null;

          const categories = (disciplineEntry?.categories || [])
            .map((categoryEntry) => {
              const category = getCategory(discipline, categoryEntry?.categoryId);

              if (!category?.name) return null;

              const subCategories = (categoryEntry?.subCategories || [])
                .map((subCategoryEntry) => {
                  const subCategory = getSubCategory(category, subCategoryEntry?.subCategoryId);
                  if (!subCategory?.name) return null;
                  return {
                    id: subCategoryEntry.subCategoryId,
                    name: subCategory.name,
                    makeIds: Array.isArray(subCategoryEntry.makeIds)
                      ? [...new Set(subCategoryEntry.makeIds.map(String))]
                      : [],
                  };
                })
                .filter(Boolean);

              return {
                id: categoryEntry?.categoryId,
                name: category.name,
                subCategories,
              };
            })
            .filter(Boolean);

          if (!categories.length) return null;

          return {
            id: disciplineEntry?.disciplineId,
            name: discipline.name,
            categories,
          };
        })
        .filter(Boolean);

      if (!vendorType || !disciplines.length) return null;

      return {
        vendorType,
        disciplines,
      };
    })
    .filter(Boolean);

const VendorCategoriesView = ({
  className = "",
  isMobileView = false,
  profile,
  showHeader = true,
  showViewOnlyBadge = true,
  showMakes = false,
  readOnly = false,
  vendorCategories: vendorCategoriesProp,
}) => {
  const [taxonomyAll, setTaxonomyAll] = useState({});
  const [selectedCategoryByType, setSelectedCategoryByType] = useState({});
  const [selectedDivisionsByType, setSelectedDivisionsByType] = useState({});
  const [collapsedVendorTypes, setCollapsedVendorTypes] = useState({});
  const [makeLookup, setMakeLookup] = useState({ key: "", status: "idle", byId: {} });
  const [makeRetry, setMakeRetry] = useState(0);
  const vendorCategories = useMemo(() => {
    if (Array.isArray(vendorCategoriesProp)) return vendorCategoriesProp;
    if (Array.isArray(profile?.vendorCategories)) return profile.vendorCategories;
    return [];
  }, [profile?.vendorCategories, vendorCategoriesProp]);
  const selectedMakeKey = useMemo(() => {
    const makeIds = new Set();
    for (const type of vendorCategories) {
      if (type.vendorType !== "Material") continue;
      for (const discipline of type.disciplines || []) {
        for (const category of discipline.categories || []) {
          for (const sub of category.subCategories || []) {
            for (const makeId of sub.makeIds || []) makeIds.add(String(makeId));
          }
        }
      }
    }
    return JSON.stringify([...makeIds].sort());
  }, [vendorCategories]);
  const makeStatus = makeLookup.key === selectedMakeKey ? makeLookup.status : "loading";
  const mappedVendorCategories = useMemo(
    () => buildMappedVendorCategories(vendorCategories, taxonomyAll),
    [taxonomyAll, vendorCategories],
  );
  const taxonomySummary = useMemo(() => {
    const divisions = new Set();
    const categories = new Set();
    const subCategories = new Set();
    const brands = new Set();

    mappedVendorCategories.forEach((typeEntry) => {
      typeEntry.disciplines.forEach((discipline) => {
        const divisionKey = `${typeEntry.vendorType}:${discipline.id || discipline.name}`;
        divisions.add(divisionKey);

        discipline.categories.forEach((category) => {
          const categoryKey = `${divisionKey}:${category.id || category.name}`;
          categories.add(categoryKey);

          category.subCategories.forEach((subCategory) => {
            subCategories.add(`${categoryKey}:${subCategory.id || subCategory.name}`);
            subCategory.makeIds.forEach((makeId) => brands.add(String(makeId)));
          });
        });
      });
    });

    return [
      {
        key: "divisions",
        label: "Divisions",
        count: divisions.size,
        style: "bg-[#EFF6FF] text-[#2563EB]",
      },
      {
        key: "categories",
        label: "Categories",
        count: categories.size,
        style: "bg-[#F5F3FF] text-[#7C3AED]",
      },
      {
        key: "subCategories",
        label: "Sub-categories",
        count: subCategories.size,
        style: "bg-[#ECFDF5] text-[#059669]",
      },
      {
        key: "brands",
        label: "Brands",
        count: brands.size,
        style: "bg-[#FFF7ED] text-[#EA580C]",
      },
    ];
  }, [mappedVendorCategories]);
  useEffect(() => {
    const selectedMakeIds = JSON.parse(selectedMakeKey);
    if (!showMakes || !selectedMakeIds.length) return undefined;
    const controller = new AbortController();
    setMakeLookup({ key: selectedMakeKey, status: "loading", byId: {} });

    const loadMakes = async () => {
      const byId = {};
      // The lookup accepts at most 1,000 selected IDs; avoid fetching unselected make options.
      for (let start = 0; start < selectedMakeIds.length; start += 1000) {
        if (controller.signal.aborted) return;
        const { selectedMakes } = await lookupVendorMakes(
          [],
          selectedMakeIds.slice(start, start + 1000),
          controller.signal,
        );
        for (const make of selectedMakes) {
          byId[String(make._id)] = { name: make.name, isActive: make.isActive };
        }
      }
      if (!controller.signal.aborted) setMakeLookup({ key: selectedMakeKey, status: "ready", byId });
    };

    loadMakes().catch(() => {
      if (!controller.signal.aborted) setMakeLookup({ key: selectedMakeKey, status: "error", byId: {} });
    });
    return () => controller.abort();
  }, [showMakes, selectedMakeKey, makeRetry]);

  useEffect(() => {
    let isMounted = true;

    if (!vendorCategories.length) return undefined;

    getVendorTaxonomy()
      .then((taxonomyData) => {
        if (isMounted) setTaxonomyAll(taxonomyData);
      })
      .catch((error) => {
        console.error("Failed to fetch vendor taxonomy", error);
      });

    return () => {
      isMounted = false;
    };
  }, [vendorCategories.length]);

  if (!mappedVendorCategories.length) return null;

  const selectCategory = (vendorType, categoryKey) => {
    setSelectedCategoryByType((previousState) => ({
      ...previousState,
      [vendorType]: categoryKey,
    }));
  };

  const renderMakes = (subCategory, compact = false) => {
    const textSizeClass = compact ? "text-[10px]" : "text-[11px]";

    if (!subCategory.makeIds.length) {
      return <span title="No makes added" className="text-xs text-[#9CA3AF]">—</span>;
    }

    if (makeStatus === "loading") {
      return <span role="status" className={`${textSizeClass} text-[#6B7280]`}>Loading makes...</span>;
    }

    if (makeStatus === "error") {
      return (
        <span className={`${textSizeClass} text-[#6B7280]`}>
          {subCategory.makeIds.length} selected
        </span>
      );
    }

    return (
      <div
        role="group"
        aria-label={`Makes for ${subCategory.name}`}
        className="flex min-w-0 flex-wrap gap-1.5"
      >
        {subCategory.makeIds.map((makeId) => {
          const make = makeLookup.byId[makeId];
          const label = make?.name || `Unavailable make (${makeId.slice(-6)})`;
          const inactive = make?.isActive === false;

          return (
            <span
              key={makeId}
              title={make?.name ? `${make.name}${inactive ? " (inactive)" : ""}` : makeId}
              className={`max-w-full break-words rounded-full border px-2.5 py-1 ${textSizeClass} leading-4 ${!make?.name || inactive ? "border-[#E5E7EB] bg-[#F9FAFB] text-[#6B7280]" : "border-[#F4D3BA] bg-[#FFF0E4] text-[#9A4B13]"}`}
            >
              {label}{inactive ? " (inactive)" : ""}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <section
      className={`${className || (isMobileView ? "mx-3 my-4" : "mx-[46px] my-8")} rounded-2xl border border-[#E8E6DD] bg-white p-5 shadow-sm`}
    >
      {showHeader && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-[#1F2937]">Categories</h2>
          {showViewOnlyBadge && (
            <span className="rounded-full bg-[#EEF4FF] px-3 py-1 text-xs font-semibold text-[#185FA5]">
              View only
            </span>
          )}
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {taxonomySummary.map((item) => (
          <div
            key={item.key}
            className="flex min-w-0 items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white px-3 py-3 shadow-sm"
          >
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${item.style}`}>
              <TaxonomySummaryIcon type={item.key} />
            </span>
            <div className="min-w-0">
              <p className="text-2xl font-semibold leading-6 text-[#1F2937]">
                {item.count}
              </p>
              <p className="mt-1 truncate text-[11px] font-medium text-[#667085]">
                {item.label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {showMakes && selectedMakeKey !== "[]" && makeStatus === "error" && (
        <div role="alert" className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <span>Unable to load selected make names. Vendor categories are still shown.</span>
          <button
            type="button"
            onClick={() => setMakeRetry((value) => value + 1)}
            className="font-semibold underline"
          >
            Retry
          </button>
        </div>
      )}

      <div className="space-y-4">
        {mappedVendorCategories.map((typeEntry) => {
          const categoryOptions = typeEntry.disciplines.flatMap(
            (disciplineEntry) =>
              disciplineEntry.categories.map((categoryEntry) => ({
                ...categoryEntry,
                key: `${typeEntry.vendorType}-${disciplineEntry.id}-${categoryEntry.id}`,
                disciplineId: disciplineEntry.id,
                disciplineName: disciplineEntry.name,
              })),
          );
          const requestedCategoryKey = selectedCategoryByType[typeEntry.vendorType];
          const availableDivisionIds = typeEntry.disciplines.map((discipline) =>
            String(discipline.id),
          );
          const storedDivisionIds = selectedDivisionsByType[typeEntry.vendorType] || [];
          const validStoredDivisionIds = storedDivisionIds.filter((divisionId) =>
            availableDivisionIds.includes(String(divisionId)),
          );
          const selectedDivisionIds = readOnly
            ? availableDivisionIds
            : validStoredDivisionIds.length
              ? validStoredDivisionIds
              : availableDivisionIds;
          const selectedDivisionCategories = categoryOptions.filter((category) =>
            selectedDivisionIds.includes(String(category.disciplineId)),
          );
          let categorySerialNumber = 0;
          const selectedDivisionGroups = typeEntry.disciplines
            .filter((discipline) =>
              selectedDivisionIds.includes(String(discipline.id)),
            )
            .map((discipline) => ({
              id: discipline.id,
              name: discipline.name,
              categories: categoryOptions
                .filter((category) =>
                  isSameId(category.disciplineId, discipline.id),
                )
                .map((category) => ({
                  ...category,
                  serialNumber: ++categorySerialNumber,
                })),
            }));
          const selectedCategory =
            selectedDivisionCategories.find(
              (category) => category.key === requestedCategoryKey,
            ) || selectedDivisionCategories[0];
          const selectedDiscipline =
            typeEntry.disciplines.find((discipline) =>
              isSameId(discipline.id, selectedCategory?.disciplineId),
            ) || typeEntry.disciplines[0];
          const showMakesColumn = showMakes && typeEntry.vendorType === "Material";
          const isVendorTypeExpanded = !collapsedVendorTypes[typeEntry.vendorType];
          const vendorTypeContentId = `vendor-type-${typeEntry.vendorType
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")}-content`;

          return (
            <div
              key={typeEntry.vendorType}
              className="overflow-hidden rounded-xl border border-[#DDE3EC] bg-white"
            >
              <button
                type="button"
                onClick={() =>
                  setCollapsedVendorTypes((previousState) => ({
                    ...previousState,
                    [typeEntry.vendorType]: isVendorTypeExpanded,
                  }))
                }
                aria-expanded={isVendorTypeExpanded}
                aria-controls={vendorTypeContentId}
                className={`flex w-full flex-wrap items-center justify-between gap-2 bg-[#F8FAFC] px-4 py-3 text-left transition hover:bg-[#F1F5F9] ${isVendorTypeExpanded ? "border-b border-[#DDE3EC]" : ""}`}
              >
                <span className="inline-flex rounded-full border border-[#D9DEE7] bg-white px-3 py-1 text-xs font-semibold text-[#374151]">
                  {typeEntry.vendorType}
                </span>
                <span className="flex items-center gap-3 text-[11px] font-medium text-[#6B7280]">
                  <span>
                    {typeEntry.disciplines.length}{" "}
                    {typeEntry.disciplines.length === 1 ? "division" : "divisions"}
                    <span className="mx-1.5 text-[#CBD5E1]">|</span>
                    {categoryOptions.length}{" "}
                    {categoryOptions.length === 1 ? "category" : "categories"}
                  </span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                    className={`shrink-0 text-[#64748B] transition-transform ${isVendorTypeExpanded ? "rotate-180" : ""}`}
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>

              {isVendorTypeExpanded && (
                <div id={vendorTypeContentId}>
                  <div className="border-b border-[#E7EBF1] bg-white px-4 py-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#64748B]">
                    Divisions
                  </p>
                  <p className="hidden text-[10px] text-[#94A3B8] sm:block">
                    {readOnly
                      ? "All mapped divisions are shown"
                      : "Select a division to view its categories"}
                  </p>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-wrap lg:overflow-visible lg:pb-0">
                  {typeEntry.disciplines.map((disciplineEntry) => {
                    const divisionId = String(disciplineEntry.id);
                    const isSelected = selectedDivisionIds.includes(divisionId);

                    return (
                      <button
                        key={`${typeEntry.vendorType}-${disciplineEntry.id}`}
                        type="button"
                        disabled={readOnly}
                        onClick={() => {
                          if (readOnly) return;
                          if (isSelected && selectedDivisionIds.length === 1) return;

                          const nextDivisionIds = isSelected
                            ? selectedDivisionIds.filter(
                              (selectedId) => selectedId !== divisionId,
                            )
                            : [...selectedDivisionIds, divisionId];

                          setSelectedDivisionsByType((previousState) => ({
                            ...previousState,
                            [typeEntry.vendorType]: nextDivisionIds,
                          }));

                          if (
                            !nextDivisionIds.includes(
                              String(selectedCategory?.disciplineId),
                            )
                          ) {
                            const nextCategory = categoryOptions.find((category) =>
                              nextDivisionIds.includes(String(category.disciplineId)),
                            );
                            if (nextCategory) {
                              selectCategory(typeEntry.vendorType, nextCategory.key);
                            }
                          }
                        }}
                        aria-pressed={isSelected}
                        className={`flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition disabled:cursor-default ${isSelected ? "border-[#93C5FD] bg-[#EFF6FF] font-semibold text-[#1D4ED8]" : "border-[#E2E8F0] bg-white text-[#475569] hover:border-[#BFDBFE] hover:bg-[#F8FBFF]"}`}
                      >
                        <span
                          aria-hidden="true"
                          className={`inline-flex h-4 w-4 items-center justify-center rounded text-[10px] ${isSelected ? "bg-[#2563EB] text-white" : "border border-[#CBD5E1] bg-white text-transparent"}`}
                        >
                          ✓
                        </span>
                        <span>{disciplineEntry.name}</span>
                        <span className={`rounded-full px-1.5 py-0.5 text-[9px] ${isSelected ? "bg-white text-[#2563EB]" : "bg-[#F1F5F9] text-[#64748B]"}`}>
                          {disciplineEntry.categories.length}
                        </span>
                        <span aria-hidden="true" className="text-base leading-none text-[#94A3B8]">
                          ›
                        </span>
                      </button>
                    );
                  })}
                </div>
                  </div>

                  <div className="lg:grid lg:grid-cols-[300px_minmax(0,1fr)]">
                <nav
                  aria-label={`${typeEntry.vendorType} categories from selected divisions`}
                  className="hidden max-h-[520px] overflow-y-auto bg-[#F8FAFC] lg:block"
                >
                  <div className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-2 border-b border-[#E7EBF1] px-4 py-3">
                    <span className="text-[9px] font-semibold uppercase text-[#94A3B8]">
                      Sr#
                    </span>
                    <p className="text-xs font-semibold text-[#1F2937]">Categories</p>
                    <span className="text-[10px] text-[#94A3B8]">
                      {selectedDivisionCategories.length}
                    </span>
                  </div>
                  <div className="space-y-3 p-2.5">
                    {selectedDivisionGroups.map((divisionGroup) => (
                      <section key={`${typeEntry.vendorType}-${divisionGroup.id}`}>
                        <div className="mb-1 flex items-center justify-between gap-2 rounded-md bg-[#F1EDFF] px-2.5 py-1.5">
                          <p className="min-w-0 truncate text-[10px] font-semibold uppercase tracking-wide text-[#6D28D9]">
                            Division: {divisionGroup.name}
                          </p>
                          <span className="shrink-0 rounded-full bg-white px-1.5 py-0.5 text-[9px] font-semibold text-[#6D28D9]">
                            {divisionGroup.categories.length}
                          </span>
                        </div>

                        <div className="space-y-1">
                          {divisionGroup.categories.map((categoryEntry) => {
                            const isSelected =
                              selectedCategory?.key === categoryEntry.key;

                            return (
                              <button
                                key={categoryEntry.key}
                                type="button"
                                onClick={() =>
                                  selectCategory(
                                    typeEntry.vendorType,
                                    categoryEntry.key,
                                  )
                                }
                                aria-current={isSelected ? "true" : undefined}
                                className={`grid w-full grid-cols-[28px_minmax(0,1fr)_auto] items-center gap-2 rounded-r-lg border border-transparent px-2.5 py-2.5 text-left transition ${isSelected ? "border-l-4 border-l-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]" : "border-l-4 border-l-transparent text-[#334155] hover:bg-white"}`}
                              >
                                <span className="text-center text-xs font-semibold text-[#64748B]">
                                  {categoryEntry.serialNumber}
                                </span>
                                <span className="min-w-0">
                                  <span className={`block truncate text-xs ${isSelected ? "font-semibold" : "font-medium"}`}>
                                    {categoryEntry.name}
                                  </span>
                                  <span className="mt-0.5 block text-[12px] text-[#94A3B8]">
                                    {categoryEntry.subCategories.length}{" "}
                                    {categoryEntry.subCategories.length === 1
                                      ? "sub-category"
                                      : "sub-categories"}
                                  </span>
                                </span>
                                <span
                                  aria-hidden="true"
                                  className="shrink-0 text-base text-[#94A3B8]"
                                >
                                  ›
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </div>
                </nav>

                <div className="min-w-0 border-t border-[#DDE3EC] lg:border-l lg:border-t-0">
                  <div className="border-b border-[#E7EBF1] bg-white p-3 lg:hidden">
                    <label
                      htmlFor={`category-selector-${typeEntry.vendorType}`}
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]"
                    >
                      Select category
                    </label>
                    <select
                      id={`category-selector-${typeEntry.vendorType}`}
                      value={selectedCategory?.key || ""}
                      onChange={(event) =>
                        selectCategory(typeEntry.vendorType, event.target.value)
                      }
                      className="w-full rounded-md border border-[#CBD5E1] bg-white px-3 py-2 text-xs font-medium text-[#374151] outline-none focus:border-[#185FA5]"
                    >
                      {selectedDivisionGroups.map((divisionGroup) => (
                        <optgroup
                          key={`${typeEntry.vendorType}-mobile-${divisionGroup.id}`}
                          label={divisionGroup.name}
                        >
                          {divisionGroup.categories.map((category) => (
                            <option key={category.key} value={category.key}>
                              {category.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>

                  {selectedCategory && (
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[#E7EBF1] bg-white px-4 py-3">
                        <div className="min-w-0">
                          <p className="mb-1 text-[10px] font-medium text-[#94A3B8]">
                            {typeEntry.vendorType} / {selectedDiscipline?.name}
                          </p>
                          <h3 className="break-words text-sm font-semibold text-[#1F2937]">
                            {selectedCategory.name}
                          </h3>
                          <p className="mt-1 text-[12px] text-[#6B7280]">
                            Sub-categories supplied by this vendor and their associated makes.
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-[#EAF1FC] px-2.5 py-1 text-[12px] font-semibold text-[#43648B]">
                          {selectedCategory.subCategories.length}{" "}
                          {selectedCategory.subCategories.length === 1
                            ? "sub-category"
                            : "sub-categories"}
                        </span>
                      </div>

                      {hasItems(selectedCategory.subCategories) ? (
                        <>
                          <div className="h-[420px] divide-y divide-[#E7EBF1] overflow-y-auto lg:hidden">
                            {selectedCategory.subCategories.map((subCategory, index) => (
                              <div
                                key={`mobile-${selectedCategory.key}-${subCategory.id}`}
                                className="border-b border-[#E7EBF1] p-3"
                              >
                                <div className="flex items-start gap-2.5">
                                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#F1F5F9] px-1 text-[10px] font-semibold text-[#64748B]">
                                    {index + 1}
                                  </span>
                                  <span className="min-w-0 break-words rounded-md bg-[#ECFDF5] px-2 py-1 text-[12px] font-semibold leading-4 text-[#047857]">
                                    {subCategory.name}
                                  </span>
                                </div>
                                {showMakesColumn && subCategory.makeIds.length > 0 && (
                                  <div className="mt-2 pl-[30px]">
                                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-[#C2410C]">
                                      Makes / Brands
                                    </p>
                                    {renderMakes(subCategory, true)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="hidden h-[420px] overflow-auto lg:block">
                            <table className="w-full border border-gray-300 table-fixed border-collapse text-left">
                              <thead>
                                <tr className="bg-[#F8FAFC] text-[10px] font-semibold uppercase tracking-wide text-[#6B7280]">
                                  <th
                                    scope="col"
                                    className="sticky top-0 z-10 w-[58px] border-b border-[#E7EBF1] bg-[#F8FAFC] px-3 py-2.5 text-center"
                                  >
                                    Sr#
                                  </th>
                                  <th
                                    scope="col"
                                    className={`sticky top-0 z-10 ${showMakesColumn ? "w-[30%]" : "w-auto"} border-b border-l border-[#E7EBF1] bg-[#ECFDF5] px-4 py-2.5 text-[#047857]`}
                                  >
                                    Sub-category
                                  </th>
                                  {showMakesColumn && (
                                    <th
                                      scope="col"
                                      className="sticky top-0 z-10 border-b border-l border-[#E7EBF1] bg-[#FFF7ED] px-4 py-2.5 text-[#C2410C]"
                                    >
                                      Makes / Brands
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-[#EEF1F5]">
                                {selectedCategory.subCategories.map((subCategory, index) => (
                                  <tr
                                    key={`${selectedCategory.key}-${subCategory.id}`}
                                    className="border-b border-[#E7EBF1] align-top transition-colors hover:bg-[#FAFCFF]"
                                  >
                                    <td className="px-3 py-3 text-center text-xs font-semibold text-[#6B7280]">
                                      {index + 1}
                                    </td>
                                    <th
                                      scope="row"
                                      className="border-l border-[#D1FAE5] bg-[#FBFFFD] px-4 py-3 text-left text-xs font-semibold leading-5 text-[#047857]"
                                    >
                                      <span className="inline-flex max-w-full break-words rounded-md bg-[#ECFDF5] px-2 py-1">
                                        {subCategory.name}
                                      </span>
                                    </th>
                                    {showMakesColumn && (
                                      <td className="border-l border-[#EEF1F5] px-4 py-3">
                                        {renderMakes(subCategory)}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <p className="px-4 py-6 text-center text-xs text-[#9CA3AF]">
                          No sub-categories added
                        </p>
                      )}
                    </div>
                  )}
                </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default VendorCategoriesView;
